import {
  Card,
  Icon,
  IconButton,
  Skeleton,
  Stack,
  StackProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps, SystemStyleObject } from '@mui/system';
import { AttendanceEntriesCounts, JustificationCounts } from '@schooly/api';
import { AttendanceChart, AttendanceChartProps } from '@schooly/components/charts';
import { CalendarClockIcon, CalendarTextIcon, NewTabIcon } from '@schooly/style';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAttendanceCodes } from '../../../hooks/useAttendanceCodes';
import {
  getAttendancePercentages,
  getPresentAbsentCodeIds,
  getTotalByCodeIds,
  hasJustificationCounts,
} from '../attendance-utils';
import { AttendanceStatsJustificationCard, AttendanceStatsLayout } from './AttendanceStats.styled';
import { useNavigate } from 'react-router-dom';

const CARDS_COUNT = 3;
interface AttendanceStatsProps {
  statistics: { name: string; counts: AttendanceEntriesCounts[] }[];
  loading?: boolean;
  showChartBars?: boolean;
  sx?: SxProps<Theme>;
  emptyBarTitles?: string[];
  justificationCounts?: JustificationCounts;
  justificationLink?: string;
}

export const AttendanceStats: FC<AttendanceStatsProps> = ({
  statistics,
  loading,
  justificationCounts,
  justificationLink = '/attendance/justifications',
  showChartBars = true,
  sx,
  emptyBarTitles,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { attendanceCodes: codes, isLoading: codesFetching } = useAttendanceCodes();

  const { presentStats, absentStats } = useMemo(() => {
    const { present: presentCodeIds, absent: absentCodeIds } = getPresentAbsentCodeIds(codes);

    const percentages = statistics.map((s) => {
      const present = getTotalByCodeIds(presentCodeIds, s.counts);
      const absent = getTotalByCodeIds(absentCodeIds, s.counts);
      return { name: s.name, ...getAttendancePercentages(present, absent) };
    });

    return {
      presentStats: percentages.map((p) => ({ value: p.present, name: p.name })),
      absentStats: percentages.map((p) => ({ value: p.absent, name: p.name })),
    };
  }, [codes, statistics]);

  const showJustificationCard = useMemo(() => {
    const hasJustifications = !!justificationCounts && hasJustificationCounts(justificationCounts);
    return hasJustifications || codes.some((code) => code.has_justification);
  }, [codes, justificationCounts]);

  const showStats = statistics.some((s) => s.counts.length > 0);

  const isLoading = codesFetching || loading;

  if (!isLoading && !showStats && !showJustificationCard) return null;

  return (
    <AttendanceStatsLayout
      direction="row"
      sx={(theme) => (typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject}
    >
      {isLoading ? (
        <AttendanceStatsSkeleton />
      ) : (
        <>
          {showStats && (
            <>
              <AttendanceChartCard
                title={formatMessage({ id: 'attendance-Present' })}
                statistics={presentStats}
                primaryColor={theme.palette.success.main}
                showChartBars={showChartBars}
                emptyBarTitles={emptyBarTitles}
              />
              <AttendanceChartCard
                title={formatMessage({ id: 'attendance-Absent' })}
                statistics={absentStats}
                primaryColor={theme.palette.error.main}
                showChartBars={showChartBars}
                emptyBarTitles={emptyBarTitles}
              />
            </>
          )}
          {showJustificationCard && (
            <AttendanceStatsJustifications
              justificationCounts={justificationCounts}
              justificationLink={justificationLink}
            />
          )}
        </>
      )}
    </AttendanceStatsLayout>
  );
};

type AttendanceChartCardProps = {
  title: string;
} & Omit<AttendanceChartProps, 'isHovered'>;

export const AttendanceChartCard: FC<AttendanceChartCardProps> = ({ title, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      sx={(theme) => ({
        '&:hover': {
          backgroundColor: theme.palette.common.lightBg,
          color: theme.palette.primary.main,
        },
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack p={1.5}>
        <Typography>{title}</Typography>

        <AttendanceChart isHovered={isHovered} {...props} />
      </Stack>
    </Card>
  );
};

export const AttendanceStatsSkeleton = () => {
  return [...new Array(CARDS_COUNT)].map((_, index) => (
    <Skeleton variant="rectangular" height={84} key={index} />
  ));
};

type AttendanceStatsJustificationsProps = {
  justificationCounts?: JustificationCounts;
  justificationLink?: string;
};

export const AttendanceStatsJustifications: FC<AttendanceStatsJustificationsProps> = ({
  justificationCounts,
  justificationLink = '/attendance/justifications',
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const hasJustifications = !!justificationCounts && hasJustificationCounts(justificationCounts);
  const { closed, ...countsToShow } = justificationCounts ?? {};
  const showJustificationCounts = hasJustificationCounts(countsToShow);

  return (
    <AttendanceStatsJustificationCard
      onClick={hasJustifications ? () => navigate(justificationLink) : undefined}
      actionAreaProps={{ disableRipple: !hasJustifications }}
      disabled={!hasJustifications}
      actions={
        <Stack height="100%" alignItems="flex-end" p={0.5}>
          <IconButton size="small" sx={(theme) => ({ color: theme.palette.common.grey })}>
            <NewTabIcon />
          </IconButton>
        </Stack>
      }
      children={
        <Stack p={0.5}>
          <Typography>
            {formatMessage({ id: 'attendance-justification-AbsenceJustifications' })}
          </Typography>
          <Stack direction="row" gap={1}>
            {showJustificationCounts ? (
              <>
                {!!justificationCounts?.expired && (
                  <AttendanceJustificationCounter
                    icon={<CalendarClockIcon />}
                    sx={(theme) => ({ color: theme.palette.error.main })}
                    count={justificationCounts.expired}
                  />
                )}
                {!!justificationCounts?.pending && (
                  <AttendanceJustificationCounter
                    icon={<CalendarClockIcon />}
                    sx={(theme) => ({ color: theme.palette.common.orange })}
                    count={justificationCounts.pending}
                  />
                )}
                {!!justificationCounts?.submitted && (
                  <AttendanceJustificationCounter
                    icon={<CalendarTextIcon />}
                    sx={(theme) => ({ color: theme.palette.common.orange })}
                    count={justificationCounts.submitted}
                  />
                )}
              </>
            ) : (
              <Typography variant="h1" color="text.secondary" noWrap>
                {formatMessage({
                  id: hasJustifications ? 'attendance-NoNewOnes' : 'attendance-None',
                })}
              </Typography>
            )}
          </Stack>
        </Stack>
      }
    />
  );
};

type AttendanceJustificationCounterProps = {
  icon: React.ReactNode;
  count: number;
} & StackProps;

const AttendanceJustificationCounter: FC<AttendanceJustificationCounterProps> = ({
  icon,
  count,
  ...props
}) => {
  return (
    <Stack direction="row" gap={0.5} alignItems="center" {...props}>
      <Icon>{icon}</Icon>
      <Typography variant="h1" color="inherit">
        {count}
      </Typography>
    </Stack>
  );
};
